import axios from "axios";
import i18n, { UI_TO_BACKEND_LANG } from "@/i18n";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve the API base URL.
 *
 * In production (cartonomie.fr / cartonomie.com), Emergent's edge proxies
 * `/api/*` to the FastAPI backend through the same hostname. Using a relative
 * `/api` base avoids cross-origin requests entirely — which is critical
 * because Safari's Intelligent Tracking Prevention (ITP) blocks
 * `withCredentials` requests to a different registrable domain
 * (cartonomie.fr → *.emergent.host), causing API calls to fail silently.
 *
 * We use a relative URL whenever the build-time backend host matches the
 * current page host, or whenever we're on the custom production domains.
 * Otherwise we fall back to the absolute REACT_APP_BACKEND_URL (e.g. when
 * running the frontend on localhost against a remote backend).
 */
function resolveApiBase() {
  if (typeof window === "undefined") {
    return BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
  }
  if (!BACKEND_URL) return "/api";
  try {
    const envHost = new URL(BACKEND_URL).host;
    if (envHost === window.location.host) return "/api";
  } catch {
    /* ignore parse errors, fall through */
  }
  if (/(^|\.)cartonomie\.(fr|com)$/i.test(window.location.host)) {
    return "/api";
  }
  return `${BACKEND_URL}/api`;
}

export const API = resolveApiBase();

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach current i18n language to every request as a `lang` query param so
// localized endpoints (sets, card detail, search) return data in the visitor's language.
api.interceptors.request.use((config) => {
  const ui = (i18n?.language || "en").split("-")[0];
  const lang = UI_TO_BACKEND_LANG[ui] || ui;
  config.params = { ...(config.params || {}), lang };
  return config;
});

export const LANGUAGE_FLAGS = {
  en: "EN",
  jp: "JP",
  fr: "FR",
  de: "DE",
  es: "ES",
  it: "IT",
  kr: "KR",
  zh: "ZH",
};

export const LANGUAGE_LABELS = {
  en: "English",
  jp: "Japanese",
  fr: "Français",
  de: "Deutsch",
  es: "Español",
  it: "Italiano",
  kr: "Korean",
  zh: "Chinese",
};

export const CONDITION_LABELS = {
  mint: "Mint",
  near_mint: "Near Mint",
  excellent: "Excellent",
  good: "Good",
  played: "Played",
  poor: "Poor",
};

export const SOURCE_LABELS = {
  ebay: "eBay",
  cardmarket: "Cardmarket",
  tcgplayer: "TCGPlayer",
  yahoo_jp: "Yahoo Japan",
  mercari: "Mercari",
  vinted: "Vinted",
  other: "Other",
};

export function formatPrice(value, currency = "EUR") {
  if (value === null || value === undefined) return "—";
  const symbol = { EUR: "€", USD: "$", JPY: "¥", GBP: "£" }[currency] || currency + " ";
  return `${symbol}${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Unexpected error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(", ");
  return JSON.stringify(detail);
}

export default api;

/**
 * Pick the localized name for a card or set based on the active language.
 * Falls back to the canonical (English) name.
 *   const localized = pickLocalizedName(card, i18n.language);
 */
export function pickLocalizedName(item, lang) {
  if (!item) return "";
  const map = item?.localized_names || {};
  if (lang && map[lang]) return map[lang];
  // i18n lang can be 'fr-FR' — try the base too
  if (lang && lang.includes("-")) {
    const base = lang.split("-")[0];
    if (map[base]) return map[base];
  }
  // try japanese alias
  if (lang === "ja" && map["ja"]) return map["ja"];
  return item.name || "";
}
